import React from 'react';
import { useFormContext, useFormState } from 'react-hook-form';
import clsx from 'clsx';
import { QuarterSpinner } from '../Spinners/QuarterSpinner';
import { Button as ButtonUI } from '../../ui/button';
import { cn } from '../../lib/utils';

export type ButtonProps = React.ComponentProps<typeof ButtonUI> & {
  loading?: boolean;
  icon?: string;
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      type = 'button',
      className = '',
      variant = 'primary',
      size = 'md',
      icon,
      disabled,
      loading,
      onClickCapture,
      children,

      ...props
    },
    ref
  ) => {
    const formState = useFormState();
    loading =
      loading ?? (formState.isValidating || formState.isLoading || formState.isSubmitting) ?? false;
    disabled =
      disabled ?? (loading || ((type === 'submit' || type === 'reset') && !formState.isDirty));
    const ctx = useFormContext();

    if (type === 'reset' && ctx) {
      onClickCapture = () => {
        ctx.reset();
      };
    }

    return (
      <ButtonUI
        ref={ref}
        type={type}
        disabled={disabled}
        variant={variant}
        size={size}
        className={cn(className)}
        onClickCapture={onClickCapture}
        {...props}
      >
        <div className="flex relative gap-2 items-center capitalize">
          {loading && (
            <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
              <QuarterSpinner variant={variant || undefined} />
            </span>
          )}

          {icon ? (
            <span
              className={clsx({ invisible: loading }, 'no-underline material-symbols-outlined')}
            >
              {icon}
            </span>
          ) : null}
          <p className={clsx({ invisible: loading }, 'flex items-center gap-1 mb-0.5')}>
            {children}
          </p>
        </div>
      </ButtonUI>
    );
  }
);

Button.displayName = 'Button';
